// src/pages/HrMentorsPage.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog } from '../components/ui/dialog';
import { useTranslation } from 'react-i18next';
import ChatPopup from '../components/ChatPopup';
import { authApi, mentoringApi as api, MENTORING_API } from '../axiosInstance';

/* ---------------------- Helper ---------------------- */

// Avatar-URL robust bestimmen
function getAvatarSrc(user) {
  if (user?.avatar && typeof user.avatar === 'string') {
    const a = user.avatar.trim();
    if (a.startsWith('data:') || a.startsWith('http')) return a;
  }
  const path = user?.avatar_path || user?.avatarPath;
  if (path) return `${MENTORING_API.replace(/\/$/, '')}/uploads/${path}`;
  // 1x1 transparent
  return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';
}

// Metadaten / Formatting
const pickMeta = (p) => p?.active_meta || p?.current_matching || null;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '–');
const fmtScore = (m) => m && (m.score ?? m.overall_score ?? m.total_score ?? null);

// customer_id aus verschiedenen Formen von whoami ziehen
function extractCustomerIdFromAuth(who) {
  const u = who?.user ?? who?.data?.user ?? who?.profile ?? who?.hr_profile ?? null;
  const cands = [
    // ergänzt: client_id / clientId zuerst prüfen
    u?.client_id,
    u?.clientId,
    u?.customer_id,
    u?.customerId,
    u?.tenant_id,
    u?.tenantId,
    u?.customer?.id,
    u?.tenant?.id,
  ];
  for (const v of cands) {
    const n = Number(v);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}
function extractCustomerIdFromMentoring(m) {
  const u = m?.user ?? m?.data?.user ?? null;
  const cands = [u?.client_id, u?.customer_id, u?.clientId, u?.customerId];
  for (const v of cands) {
    const n = Number(v);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return null;
}

/* ---------- Globaler HR-Projektfilter (wie HrOverviewPage) ---------- */
const PROJECT_FILTER_KEY = 'hr_overview_project_filter_v1';

function readSavedProjectFilter() {
  try {
    return localStorage.getItem(PROJECT_FILTER_KEY) || '';
  } catch {
    return '';
  }
}

function saveProjectFilter(v) {
  try {
    if (v) localStorage.setItem(PROJECT_FILTER_KEY, String(v));
    else localStorage.removeItem(PROJECT_FILTER_KEY);
  } catch {}
}

function getSurveyIdFromSearch(search) {
  try {
    const sp = new URLSearchParams(search || '');
    const v = sp.get('surveyId') || sp.get('survey_id') || '';
    return v ? String(v) : '';
  } catch {
    return '';
  }
}

/* ---------------------- Page ---------------------- */

export default function HrMentorsPage() {
  const [mentors, setMentors] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);

  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name | score | confirmed

  // NEU: Initialfilter aus URL (?surveyId=) oder globalem HR-Filter
  const location = useLocation();
  const urlSurveyId = getSurveyIdFromSearch(location.search);
  const isLockedByOverview = Boolean(urlSurveyId);

  const [projectFilter, setProjectFilter] = useState(() => {
    return urlSurveyId || readSavedProjectFilter() || '';
  });

  const [allProjectIds, setAllProjectIds] = useState([]);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [hrChatConfig, setHrChatConfig] = useState(null);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // NEU: Wenn Navigation mit anderer surveyId erfolgt, Filter automatisch übernehmen
  useEffect(() => {
    const fromUrl = getSurveyIdFromSearch(location.search);
    if (fromUrl) {
      setProjectFilter(fromUrl);
      saveProjectFilter(fromUrl);
      return;
    }
    const saved = readSavedProjectFilter() || '';
    setProjectFilter((prev) => (prev !== saved ? saved : prev));
  }, [location.search]);

  // ---- Laden (robust mit Fallback) ----
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError('');

      try {
        // 1) Versuche zuerst AUTH (/auth/whoami via authApi)
        let customerId = null;
        try {
          const { data: who } = await authApi.get('whoami', { withCredentials: true, timeout: 6000 });
          customerId = extractCustomerIdFromAuth(who);
        } catch {
          // Ignorieren – wir haben ja Fallback
        }

        // 2) Fallback: Mentoring-Session (/mentoring/whoami via mentoringApi)
        if (!customerId) {
          const { data: m } = await api.get('whoami', { withCredentials: true, timeout: 6000 });
          customerId = extractCustomerIdFromMentoring(m);
        }

        if (!alive) return;

        if (!Number.isInteger(customerId) || customerId <= 0) {
          setMentors([]);
          setFiltered([]);
          setAllProjectIds([]);
          setError(t('errorLoading', 'Fehler beim Laden.'));
          setLoading(false);
          return;
        }

        // 3) Mandantenspezifische Liste holen (KEIN doppeltes Präfix!)
        const { data } = await api.get('mentors/ready', {
          params: { customer_id: customerId },
          withCredentials: true,
          timeout: 10000,
        });

        const rows = Array.isArray(data) ? data : [];
        const mapped = rows.map((r) => ({ ...r, avatarPath: r.avatar_path }));

        // 4) verfügbare Projekt-IDs ableiten
        const uniqueSurveyIds = Array.from(
          new Set(
            mapped
              .map((m) => m.survey_id ?? m.active_meta?.survey_id ?? m.current_matching?.survey_id ?? null)
              .filter((v) => v != null)
              .map(String)
          )
        );

        setAllProjectIds(uniqueSurveyIds);
        setMentors(mapped);
        setFiltered(mapped);
      } catch (e) {
        console.error('load mentors failed:', e);
        if (!alive) return;
        setError(t('errorLoading', 'Fehler beim Laden.'));
        setMentors([]);
        setFiltered([]);
        setAllProjectIds([]);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [t]);

  // ---- Suche / Filter / Sortierung ----
  useEffect(() => {
    let results = [...mentors];

    if (search.trim()) {
      const s = search.toLowerCase();
      results = results.filter(
        (m) =>
          `${m.first_name || ''} ${m.last_name || ''}`.toLowerCase().includes(s) ||
          (m.abteilung || '').toLowerCase().includes(s) ||
          (m.job_title || '').toLowerCase().includes(s) ||
          (m.email || '').toLowerCase().includes(s)
      );
    }

    if (projectFilter) {
      results = results.filter((m) => {
        const sid = m.survey_id ?? m.active_meta?.survey_id ?? m.current_matching?.survey_id ?? null;
        return sid && String(sid) === String(projectFilter);
      });
    }

    if (sortBy === 'name') {
      results.sort((a, b) =>
        (`${a.last_name || ''}${a.first_name || ''}`).localeCompare(`${b.last_name || ''}${b.first_name || ''}`)
      );
    } else if (sortBy === 'score') {
      results.sort((a, b) => {
        const sa = fmtScore(pickMeta(a)) ?? 0;
        const sb = fmtScore(pickMeta(b)) ?? 0;
        return sb - sa;
      });
    } else if (sortBy === 'confirmed') {
      results.sort((a, b) => {
        const da = pickMeta(a)?.confirmed_at ? new Date(pickMeta(a).confirmed_at).getTime() : 0;
        const db = pickMeta(b)?.confirmed_at ? new Date(pickMeta(b).confirmed_at).getTime() : 0;
        return db - da;
      });
    }

    setFiltered(results);
  }, [search, sortBy, projectFilter, mentors]);

  const hasAny = useMemo(() => filtered.length > 0, [filtered]);

  // HR-Chat mit Mentor öffnen
  function openHrChatForMentor(mentorObj) {
    const meta = pickMeta(mentorObj);
    const menteeId = Number(meta?.mentee_id) || null;
    const mentorId = Number(mentorObj?.user_id ?? mentorObj?.id) || null;

    setHrChatConfig({
      menteeId,
      mentorId,
      senderRole: 'hr',
      receiverRole: 'mentor',
      threadType: 'hr',
      presetMessage: '',
    });
  }

  /* ---------------------- UI ---------------------- */

  return (
    <div className="min-h-screen bg-black text-white p-6 space-y-6">
      <button
        onClick={() => navigate('/hr/overview')}
        className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition"
      >
        ← {t('backToHRDashboard', 'Zurück zum HR-Dashboard')}
      </button>

      <h1 className="text-3xl font-bold">{t('allMentors', 'Alle Mentoren')}</h1>

      {/* Suche / Filter / Sortierung */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <input
          type="text"
          placeholder={t('searchMentorPlaceholder', 'Suche nach Name, Abteilung, Jobtitel')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-1/2 p-2 rounded bg-gray-800 text-white focus:outline-none"
        />
        <div className="flex gap-2">
          <select
            value={projectFilter}
            disabled={isLockedByOverview}
            onChange={(e) => {
              if (isLockedByOverview) return;

              const v = e.target.value || '';
              setProjectFilter(v);
              saveProjectFilter(v); // NEU: global synchron halten
            }}
            className={
              'p-2 rounded bg-gray-800 text-white focus:outline-none' +
              (isLockedByOverview ? ' opacity-70 cursor-not-allowed' : '')
            }
          >
            {!isLockedByOverview && <option value="">{t('allProjects', 'Alle Projekte')}</option>}
            {allProjectIds.map((id) => (
              <option value={id} key={id}>
                {t('projectId', 'Projekt-ID')}: {id}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="p-2 rounded bg-gray-800 text-white focus:outline-none"
          >
            <option value="name">{t('sortByName', 'Sortiere nach Name (A–Z)')}</option>
            <option value="score">{t('sortByScore', 'Sortiere nach Score')}</option>
            <option value="confirmed">{t('sortByConfirmed', 'Sortiere nach Bestätigt am')}</option>
          </select>
        </div>
      </div>

      {loading && <div className="text-gray-300">{t('loading', 'Lade…')}</div>}
      {error && <div className="text-red-400">{error}</div>}

      {!loading && !hasAny && (
        <div className="text-gray-300">
          {t('noReadyMentors', 'Keine passenden Mentoren gefunden (Onboarding, Profil & Analyse vollständig).')}
        </div>
      )}

      {/* Karten */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((mentor) => {
          const meta = pickMeta(mentor);
          return (
            <div
              key={mentor.user_id ?? `${mentor.email}-${mentor.first_name}-${mentor.last_name}`}
              onClick={() => setSelectedMentor(mentor)}
              className="bg-gray-800 rounded-2xl p-6 flex items-center gap-4 shadow-md cursor-pointer hover:bg-gray-700"
            >
              <img
                src={getAvatarSrc(mentor)}
                alt="Avatar"
                className="w-16 h-16 rounded-full object-cover border border-gray-600"
              />
              <div className="flex-1">
                <div className="text-lg font-semibold flex items-center gap-2">
                  {mentor.first_name} {mentor.last_name}
                  {mentor.has_active_match && (
                    <span
                      title={t('activeMatching', 'Aktives Matching')}
                      className="inline-block bg-green-400 text-black text-xs font-bold px-2 py-0.5 rounded-full ml-2"
                    >
                      ✓
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-400">
                  {mentor.abteilung || '—'} · {mentor.job_title || '—'}
                </div>
                {meta?.survey_id && (
                  <div className="text-xs text-green-400 font-semibold mt-1">
                    {t('projectId', 'Projekt-ID')}: {meta.survey_id}
                  </div>
                )}
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openHrChatForMentor(mentor);
                }}
                className="ml-3 shrink-0 px-3 py-1 rounded-full bg-mentixxgreen text-black font-semibold hover:bg-green-400"
                title={t('chatWithMentor', 'Chat mit Mentor öffnen')}
              >
                {t('chat', 'Chat')}
              </button>
            </div>
          );
        })}
      </div>

      {/* Detail-Dialog */}
      {selectedMentor && (
        <Dialog open={true} onOpenChange={() => setSelectedMentor(null)}>
          <div className="bg-gray-900 text-white p-6 rounded-xl w-full max-w-md space-y-4">
            <div className="flex items-center gap-4">
              <img
                src={getAvatarSrc(selectedMentor)}
                alt="Avatar"
                className="w-16 h-16 rounded-full object-cover border border-gray-600"
              />
              <div>
                <div className="text-xl font-semibold">
                  {selectedMentor.first_name} {selectedMentor.last_name}
                  {selectedMentor.has_active_match && (
                    <span
                      title={t('activeMatching', 'Aktives Matching')}
                      className="inline-block bg-green-500 text-black text-xs font-bold px-2 py-0.5 rounded-full ml-2"
                    >
                      {t('matchingActive', 'Matching aktiv')}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-400">
                  {selectedMentor.abteilung || '—'} · {selectedMentor.job_title || '—'}
                </div>
                {pickMeta(selectedMentor)?.survey_id && (
                  <div className="text-xs text-green-400 font-semibold mt-1">
                    {t('projectId', 'Projekt-ID')}: {pickMeta(selectedMentor).survey_id}
                  </div>
                )}
              </div>
            </div>

            {(() => {
              const meta = pickMeta(selectedMentor);
              return (
                <div className="text-sm space-y-1">
                  <div>
                    <strong>{t('email', 'E-Mail')}:</strong> {selectedMentor.email}
                  </div>
                  <div>
                    <strong>{t('phone', 'Telefon')}:</strong> {selectedMentor.phone || '–'}
                  </div>
                  <div>
                    <strong>{t('score', 'Score')}:</strong> {fmtScore(meta) ?? '–'}
                  </div>
                  <div>
                    <strong>{t('status', 'Status')}:</strong> {meta?.status ?? '–'}
                  </div>
                  <div>
                    <strong>{t('confirmedAt', 'Bestätigt am')}:</strong> {fmtDate(meta?.confirmed_at)}
                  </div>
                </div>
              );
            })()}

            <div className="pt-2">
              <button
                onClick={() => openHrChatForMentor(selectedMentor)}
                className="w-full px-4 py-2 rounded-full bg-mentixxgreen text-black font-bold hover:bg-green-400"
              >
                {t('chatWithMentor', 'Chat mit Mentor öffnen')}
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* HR↔Mentor Chat-Popup */}
      {hrChatConfig && (
        <ChatPopup
          menteeId={hrChatConfig.menteeId /* kann null sein */}
          mentorId={hrChatConfig.mentorId}
          senderRole={hrChatConfig.senderRole}
          receiverRole={hrChatConfig.receiverRole}
          threadType={hrChatConfig.threadType}
          presetMessage={hrChatConfig.presetMessage}
          onClose={() => setHrChatConfig(null)}
        />
      )}
    </div>
  );
}
